import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppToolbar } from '../../components/AppToolbar';
import { AppDialog } from '../../components/AppDialog';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys } from '../../components/hotkeys';
import { useUnsavedGuard, resetUnsavedTracking } from '../../hooks/useUnsavedGuard';
import { fmtLocalDateTime } from '../../utils/datetime';
import './SdmsSequenceSetupPage.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings ▸ SDMS ▸ Sequence Setup — bespoke master-detail page (ADR-0015).
//
// Visual + behavioural parity with the owner-approved Sequence-Setup-v4 prototype:
// template LIST on the left, always-live EDITOR on the right (tabs Sequence Setup ·
// Sequence Test), Copy Sequence to Selected / Copy From…, Quick presets, drag
// reorder, per-row QC type, "Record N of M", "Last updated by". A sanctioned
// deviation from standing rule 12, built on the shared services (FormTitle audit
// chip, AppToolbar incl. the shared Export to, AppDialog, confirm, notifications,
// hotkeys, unsaved guard).
//
// The whole setup (header + three QC lanes) is ONE client-side draft saved by ONE
// Save: header POST/PUT, then each lane reconciled against what was loaded (delete
// removed → add new → re-stamp only moved/changed rows), so an untouched lane
// writes nothing. Changing a saved row's QC type re-creates that row (the run
// table stores the type on the row; there is no "change type" write).
//
// Kept from the shipped model (ADR-0015): the Matrix → Test → Method cascade to a
// real TestMethod, and the header insert-duplicate/spike-after counts stay
// SEPARATE from each Sample row's InsertAfterSample#. Template ID is the OG
// NPTemplateID lookup (editable).
//
// Sequence Test: OG web has no such screen — the prototype's tab is served by the
// REAL batch builders via POST …/sequence-test on the unsaved draft, so the preview
// is exactly what a batch Sort would build; never a browser re-derivation. It
// previews the QC Batch rule (QcSequenceBuilder.Build = OG QCBatchController.
// AddSequence — Sample QC by each row's RunEvery/InsertAfter). Since OG's 2026-10-13
// rework the Prep Batch sort uses the same row rule, and the header Frequency /
// dup-after / spike-after no longer place QC in either batch.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = '/api/v1/sdms-sequence-setup';
const FORM = 'Sequence Setup';
type Phase = 'initial' | 'sample' | 'closing';
const PHASES: Phase[] = ['initial', 'sample', 'closing'];
const PHASE_TITLE: Record<Phase, string> = { initial: 'Initial QC', sample: 'Sample QC', closing: 'Closing QC' };

interface ListRow {
  sequencePatternId: number;
  templateId: number | null;
  test: string | null;
  method: string | null;
  matrix: string | null;
  initialCount: number;
  sampleCount: number;
  closingCount: number;
}
interface Opt { id: string; name: string }
interface QcTypeOpt { id: string; name: string; qcRole?: string | null; qcRootRole?: string | null }
interface TplOpt { id: number; name: string | null }
interface Run {
  key: string;
  oid?: string;          // undefined = not on the server yet (added / re-typed / copied)
  qcTypeOid: string;
  qcTypeName: string;
  qcRole: string;
  qcRootRole: string;
  freq: number;          // RunEvery(#)Samples  (Sample lane)
  after: number;         // InsertAfterSample#  (Sample lane)
}
interface Draft {
  id: number | null;     // null = a new, unsaved setup
  matrixOid: string;
  testName: string;
  testMethodId: string;
  methodName?: string;   // display fallback until the Method list loads
  templateId: string;    // '' = no template
  freq: number;
  dupAfter: number;
  spikeAfter: number;
  isClosingQc: boolean;
  initial: Run[];
  sample: Run[];
  closing: Run[];
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}
interface ApiRun {
  oid: string; order: number; qcTypeOid: string; qcTypeName?: string | null;
  qcRole?: string | null; qcRootRole?: string | null;
  frequencyNoSamples: number; insertAfterSampleNo: number;
}
/** One Sequence Test row — produced by the REAL QC-batch builder on the server. */
interface SeqTestRow { sort: number; kind: string; qcType: string; qcRole?: string | null; sampleId: string; runno: number }
interface SeqTestInfo { samples: number; qc: number; total: number; noRuns: number }
interface JobOpt { id: string; name: string | null; sampleCount: number }
const ROLE_TOKENS = new Set(['Standard', 'Blank', 'Spike', 'Duplicate']);

// Quick presets (prototype v4). Each entry is a list of alias groups; the FIRST
// alias that exists among this test's QC types is used, missing groups skipped.
const PRESETS: Record<Phase, { label: string; groups: string[][] }[]> = {
  initial: [{ label: 'CCV·CCB·BLK·LCS', groups: [['CCV', 'CCV Low', 'CCV High'], ['CCB'], ['BLK', 'Method Blank', 'MB'], ['LCS']] }],
  sample: [
    { label: 'Duplicate·MS', groups: [['Duplicate', 'Dup', 'DUP'], ['MS']] },
    { label: 'MS·MSD', groups: [['MS'], ['MSD']] },
  ],
  closing: [{ label: 'CCV·CCB', groups: [['CCV', 'CCV Low', 'CCV High'], ['CCB']] }],
};

let tmpSeq = 0;
const tmpKey = () => `tmp-${++tmpSeq}`;
const clampInt = (v: unknown, min = 1) => Math.max(min, Number.parseInt(String(v), 10) || min);

const mapRuns = (rows: ApiRun[]): Run[] => rows.map((r) => ({
  key: r.oid, oid: r.oid, qcTypeOid: r.qcTypeOid,
  qcTypeName: r.qcTypeName ?? '', qcRole: r.qcRole ?? '', qcRootRole: r.qcRootRole ?? '',
  freq: r.frequencyNoSamples || 1, after: r.insertAfterSampleNo || 1,
}));
/** Copies of runs that will be CREATED on save (copy / preset sources). */
const asNew = (rs: Run[]): Run[] => rs.map((r) => ({ ...r, oid: undefined, key: tmpKey() }));
const runOf = (t: QcTypeOpt): Run => ({
  key: tmpKey(), qcTypeOid: t.id, qcTypeName: t.name ?? '',
  qcRole: t.qcRole ?? '', qcRootRole: t.qcRootRole ?? '', freq: 1, after: 1,
});

const emptyDraft = (): Draft => ({
  id: null, matrixOid: '', testName: '', testMethodId: '', templateId: '',
  freq: 10, dupAfter: 1, spikeAfter: 1, isClosingQc: false,
  initial: [], sample: [], closing: [],
});

/** Dirty fingerprint — only what persists (row keys + footer meta are UI-only). */
function fingerprint(d: Draft | null): string {
  if (!d) return '';
  const lane = (rs: Run[]) => rs.map((r) => [r.oid ?? '', r.qcTypeOid, r.freq, r.after]);
  return JSON.stringify([d.matrixOid, d.testName, d.testMethodId, d.templateId, d.freq,
    d.dupAfter, d.spikeAfter, d.isClosingQc, lane(d.initial), lane(d.sample), lane(d.closing)]);
}

async function fetchPattern(id: number): Promise<Draft> {
  const [h, i, s, c] = await Promise.all([
    api.get(`${BASE}/${id}`),
    ...PHASES.map((p) => api.get(`${BASE}/${id}/runs`, { params: { phase: p } })),
  ]);
  const d = h?.data?.data ?? {};
  return {
    id,
    matrixOid: d.matrixOid ?? '',
    testName: d.testName ?? '',
    testMethodId: d.testMethodId ?? '',
    methodName: d.methodName ?? '',
    templateId: d.templateId == null ? '' : String(d.templateId),
    freq: d.numberOfSamplesBetweenQcTest || 1,
    dupAfter: d.dupSamplesAfterNoOfSamples || 1,
    spikeAfter: d.spikeSamplesAfterNoOfSamples || 1,
    isClosingQc: !!d.isClosingQc,
    initial: mapRuns((i?.data?.data ?? []) as ApiRun[]),
    sample: mapRuns((s?.data?.data ?? []) as ApiRun[]),
    closing: mapRuns((c?.data?.data ?? []) as ApiRun[]),
    lastUpdatedBy: d.lastUpdatedBy ?? '',
    lastUpdatedDate: d.lastUpdatedDate ?? '',
  };
}

/** Reconcile one lane: delete removed → add new → re-stamp only what moved/changed. */
async function saveLane(id: number, phase: Phase, want: Run[], had: Run[]) {
  const keep = new Set(want.filter((r) => r.oid).map((r) => r.oid));
  const removed = had.filter((o) => o.oid && !keep.has(o.oid));
  for (const o of removed)
    await api.delete(`${BASE}/${id}/runs/${o.oid}`, { params: { phase } });

  const lane = want.map((r) => ({ ...r }));
  let added = 0;
  for (const r of lane) {
    if (r.oid) continue;
    const res = await api.post(`${BASE}/${id}/runs`, { phase, qcTypeOid: r.qcTypeOid });
    r.oid = res.data?.data?.oid;
    added++;
  }
  // A membership change renumbers server-side, so every row is re-stamped;
  // otherwise only rows whose position / counts changed are written.
  const membershipChanged = removed.length > 0 || added > 0;
  for (let idx = 0; idx < lane.length; idx++) {
    const r = lane[idx]!;
    if (!r.oid) continue;
    const origIdx = had.findIndex((o) => o.oid === r.oid);
    const orig = origIdx >= 0 ? had[origIdx] : undefined;
    const valChanged = phase === 'sample' && !!orig && (orig.freq !== r.freq || orig.after !== r.after);
    if (!orig || membershipChanged || origIdx !== idx || valChanged)
      await api.put(`${BASE}/${id}/runs/${r.oid}`, {
        phase, order: idx + 1,
        frequencyNoSamples: phase === 'sample' ? r.freq : 1,
        insertAfterSampleNo: phase === 'sample' ? r.after : 1,
      });
  }
}

/** Persist a whole draft (header + three lanes). Returns the pattern id. */
async function persist(d: Draft, orig: Draft | null): Promise<number> {
  const body = {
    matrixOid: d.matrixOid,
    testMethodId: d.testMethodId,
    templateId: d.templateId === '' ? null : Number(d.templateId),
    numberOfSamplesBetweenQcTest: d.freq,
    dupSamplesAfterNoOfSamples: d.dupAfter,
    spikeSamplesAfterNoOfSamples: d.spikeAfter,
    isClosingQc: d.isClosingQc,
  };
  let id = d.id;
  if (id == null) {
    const res = await api.post(BASE, body);
    id = Number(res.data?.data?.sequencePatternId);
  } else {
    await api.put(`${BASE}/${id}`, body);
  }
  for (const p of PHASES) await saveLane(id, p, d[p], orig?.[p] ?? []);
  return id;
}

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

/**
 * `readOnly` = Data Center ▸ SDMS Sequence Setup (nav `dc2-sdmsseq`): the SAME
 * master-detail layout, VIEW ONLY — no Sequence Test tab, no New / Delete / Copy
 * buttons, no Save / Cancel, no ticks, and every field + QC lane is display-only.
 * Only Refresh and Export remain. Editing lives in Settings ▸ SDMS ▸ Sequence Setup.
 */
export default function SdmsSequenceSetupPage({ onHome, readOnly = false }: { onHome?: () => void; readOnly?: boolean }) {
  void onHome;
  const pageId = readOnly ? 'dc2-sdmsseq' : 'set-sdms-seqsetup';
  const notify = useNotify();
  const { confirm } = useConfirm();

  // ── list ──────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<ListRow[]>([]);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [sortK, setSortK] = useState<'templateId' | 'test' | 'method' | 'matrix'>('test');
  const [sortDir, setSortDir] = useState(1);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [paneW, setPaneW] = useState(440);

  // ── editor ────────────────────────────────────────────────────────────────
  const [curId, setCurId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [original, setOriginal] = useState<Draft | null>(null);
  const [tab, setTab] = useState<'setup' | 'test'>('setup');
  const [busy, setBusy] = useState(false);

  // ── lookups ───────────────────────────────────────────────────────────────
  const [matrices, setMatrices] = useState<Opt[]>([]);
  const [tests, setTests] = useState<Opt[]>([]);
  const [methods, setMethods] = useState<Opt[]>([]);
  const [qcTypes, setQcTypes] = useState<QcTypeOpt[]>([]);
  const [templates, setTemplates] = useState<TplOpt[]>([]);

  // ── dialogs ───────────────────────────────────────────────────────────────
  const [copyFromOpen, setCopyFromOpen] = useState(false);
  const [copyFromId, setCopyFromId] = useState<number | null>(null);
  const [copyToOpen, setCopyToOpen] = useState(false);
  const [copyParts, setCopyParts] = useState({ freq: true, initial: true, sample: true, closing: true });
  const [presetAsk, setPresetAsk] = useState<{ phase: Phase; runs: Run[] } | null>(null);

  // ── Sequence Test (kept across tab switches) ──────────────────────────────
  const [seqSrc, setSeqSrc] = useState<'n' | 'j'>('n');
  const [seqN, setSeqN] = useState(10);
  const [seqRuns, setSeqRuns] = useState(1);
  const [seqJobSel, setSeqJobSel] = useState<string[]>([]);
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  const [seqRows, setSeqRows] = useState<SeqTestRow[]>([]);
  const [seqInfo, setSeqInfo] = useState<SeqTestInfo | null>(null);
  const [seqErr, setSeqErr] = useState('');
  const [seqBusy, setSeqBusy] = useState(false);

  const dirty = !readOnly && draft != null && (draft.id == null || fingerprint(draft) !== fingerprint(original));
  useUnsavedGuard(dirty);

  const loadList = useCallback(async () => {
    const t0 = performance.now();
    setListLoading(true);
    try {
      const res = await api.get(BASE);
      setRows((res.data?.data ?? []) as ListRow[]);
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Failed to load the sequence setups.');
    } finally {
      setListLoading(false);
    }
  }, [notify]);

  const loadPattern = useCallback(async (id: number) => {
    setBusy(true);
    try {
      const loaded = await fetchPattern(id);
      setCurId(id);
      setDraft(loaded);
      setOriginal(structuredClone(loaded));
    } catch {
      notify.error('Failed to load this sequence setup.');
    } finally {
      setBusy(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadList();
    api.get(`${BASE}/matrices`)
      .then((r) => setMatrices((r.data?.data ?? []) as Opt[]))
      .catch(() => notify.error('Failed to load matrices.'));
  }, [loadList, notify]);

  // Open the first setup once the list arrives (and after a delete / dropped New).
  useEffect(() => {
    if (draft == null && curId == null && rows.length > 0) void loadPattern(rows[0]!.sequencePatternId);
  }, [rows, draft, curId, loadPattern]);

  // Cascades: Matrix → Test → Method; QC types + Template IDs follow the Method.
  const matrixOid = draft?.matrixOid ?? '';
  const testName = draft?.testName ?? '';
  const testMethodId = draft?.testMethodId ?? '';
  useEffect(() => {
    if (!matrixOid) { setTests([]); return; }
    api.get(`${BASE}/tests`, { params: { matrixOid } })
      .then((r) => setTests((r.data?.data ?? []) as Opt[])).catch(() => setTests([]));
  }, [matrixOid]);
  useEffect(() => {
    if (!matrixOid || !testName) { setMethods([]); return; }
    api.get(`${BASE}/methods`, { params: { matrixOid, testName } })
      .then((r) => setMethods((r.data?.data ?? []) as Opt[])).catch(() => setMethods([]));
  }, [matrixOid, testName]);
  useEffect(() => {
    if (!testMethodId) { setQcTypes([]); setTemplates([]); return; }
    api.get(`${BASE}/qc-types`, { params: { testMethodId } })
      .then((r) => setQcTypes((r.data?.data ?? []) as QcTypeOpt[])).catch(() => setQcTypes([]));
    api.get(`${BASE}/templates`, { params: { testMethodId } })
      .then((r) => setTemplates((r.data?.data ?? []) as TplOpt[])).catch(() => setTemplates([]));
  }, [testMethodId]);
  // Sequence Test "Select Job IDs": jobs with samples pending for this test (the
  // QC Batch form's own pending list).
  useEffect(() => {
    setSeqJobSel([]);
    if (!testMethodId) { setJobs([]); return; }
    api.get(`${BASE}/jobs`, { params: { testMethodId } })
      .then((r) => setJobs((r.data?.data ?? []) as JobOpt[])).catch(() => setJobs([]));
  }, [testMethodId]);

  /** Run `fn` only after the user agrees to drop unsaved changes (rule 15 wording). */
  const guard = useCallback(async (fn: () => void | Promise<void>) => {
    if (dirty) {
      const ok = await confirm({
        title: 'Unsaved changes',
        message: 'Unsaved changes will be lost. Do you want to proceed?',
        confirmLabel: 'OK',
      });
      if (!ok) return;
    }
    await fn();
  }, [dirty, confirm]);

  // ── list view model ───────────────────────────────────────────────────────
  const configured = (r: ListRow) => r.initialCount + r.sampleCount + r.closingCount > 0;
  const matrixChips = useMemo(
    () => [...new Set(rows.map((r) => r.matrix).filter((m): m is string => !!m))].sort(), [rows]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (filter === 'Not configured' && configured(r)) return false;
        if (filter !== 'All' && filter !== 'Not configured' && r.matrix !== filter) return false;
        if (!q) return true;
        return [r.templateId, r.test, r.method, r.matrix].join(' ').toLowerCase().includes(q);
      })
      .sort((a, b) => String(a[sortK] ?? '').localeCompare(String(b[sortK] ?? ''), undefined, { numeric: true }) * sortDir);
  }, [rows, search, filter, sortK, sortDir]);

  const allVisibleChecked = visible.length > 0 && visible.every((r) => checked.has(r.sequencePatternId));
  const toggleCheck = (id: number) => setChecked((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAllVisible = () => setChecked((s) => {
    const n = new Set(s);
    if (allVisibleChecked) visible.forEach((r) => n.delete(r.sequencePatternId));
    else visible.forEach((r) => n.add(r.sequencePatternId));
    return n;
  });
  const onSort = (k: typeof sortK) => { setSortDir(sortK === k ? -sortDir : 1); setSortK(k); };
  const openRow = (id: number) => {
    if (id === curId && draft?.id === id) return;
    void guard(() => loadPattern(id));
  };
  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== ' ') return;
    e.preventDefault();
    if (e.key === ' ') { if (curId != null && !readOnly) toggleCheck(curId); return; }
    const k = visible.findIndex((r) => r.sequencePatternId === curId);
    const next = visible[Math.max(0, Math.min(visible.length - 1, k + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) openRow(next.sequencePatternId);
  };

  // Resizable list/editor gutter (prototype: drag to resize, 340–900 px).
  const onGutterDown = (e: React.MouseEvent) => {
    const x0 = e.clientX, w0 = paneW;
    const move = (m: MouseEvent) => setPaneW(Math.max(340, Math.min(900, w0 + m.clientX - x0)));
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // ── editor edits ──────────────────────────────────────────────────────────
  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const setLane = (phase: Phase, runs: Run[]) => setDraft((d) => (d ? { ...d, [phase]: runs } : d));
  const setMatrix = (v: string) => patch({ matrixOid: v, testName: '', testMethodId: '', templateId: '' });
  const setTest = (v: string) => patch({ testName: v, testMethodId: '', templateId: '' });

  // OG allows each QC type ONCE per lane (its ADD picker hides the types already in
  // the lane); every path that puts a type into a lane honours that here.
  const inLane = (phase: Phase, qcTypeOid: string, exceptIdx = -1) =>
    !!draft && draft[phase].some((r, i) => i !== exceptIdx && r.qcTypeOid === qcTypeOid);
  const addQc = (phase: Phase, qcTypeOid: string) => {
    const t = qcTypes.find((o) => o.id === qcTypeOid);
    if (!t || !draft || inLane(phase, t.id)) return;
    setLane(phase, [...draft[phase], runOf(t)]);
    if (phase === 'closing' && !draft.isClosingQc) patch({ isClosingQc: true });
  };
  const changeType = (phase: Phase, idx: number, qcTypeOid: string) => {
    const t = qcTypes.find((o) => o.id === qcTypeOid);
    if (!t || !draft || inLane(phase, t.id, idx)) return;
    // A new type means a new run row (the type lives on the row) — drop the oid so
    // Save deletes the old row and creates this one in the same position.
    setLane(phase, draft[phase].map((r, i) => (i === idx
      ? { ...r, oid: undefined, key: tmpKey(), qcTypeOid: t.id, qcTypeName: t.name ?? '',
          qcRole: t.qcRole ?? '', qcRootRole: t.qcRootRole ?? '' }
      : r)));
  };
  const removeQc = (phase: Phase, idx: number) => {
    if (!draft) return;
    setLane(phase, draft[phase].filter((_, i) => i !== idx));
  };
  const moveQc = (phase: Phase, from: number, to: number) => {
    if (!draft || from === to) return;
    const list = [...draft[phase]];
    const [m] = list.splice(from, 1);
    if (!m) return;
    list.splice(to, 0, m);
    setLane(phase, list);
  };
  const setRunNum = (phase: Phase, idx: number, k: 'freq' | 'after', v: unknown) => {
    if (!draft) return;
    setLane(phase, draft[phase].map((r, i) => (i === idx ? { ...r, [k]: clampInt(v) } : r)));
  };

  const applyPreset = (phase: Phase, label: string) => {
    if (!draft) return;
    const def = PRESETS[phase].find((p) => p.label === label);
    if (!def) return;
    const byName = (n: string) => qcTypes.find((t) => (t.name ?? '').trim().toLowerCase() === n.toLowerCase());
    const runs = [...new Map(def.groups
      .map((g) => g.map(byName).find((t): t is QcTypeOpt => !!t))
      .filter((t): t is QcTypeOpt => !!t)
      .map((t) => [t.id, t] as const)).values()]   // a preset never lists a type twice
      .map(runOf);
    if (!runs.length) {
      notify.info(`None of ${label} are QC types for this test.`);
      return;
    }
    if (draft[phase].length) { setPresetAsk({ phase, runs }); return; }
    setLane(phase, runs);
    if (phase === 'closing') patch({ isClosingQc: true });
  };
  const resolvePreset = (mode: 'append' | 'replace') => {
    if (!presetAsk || !draft) return;
    const { phase, runs } = presetAsk;
    // Append adds only the preset types the lane doesn't already hold.
    const fresh = runs.filter((r) => !draft[phase].some((x) => x.qcTypeOid === r.qcTypeOid));
    setLane(phase, mode === 'append' ? [...draft[phase], ...fresh] : runs);
    if (phase === 'closing') patch({ isClosingQc: true });
    setPresetAsk(null);
  };

  const newRecord = () => void guard(() => {
    setCurId(null);
    setDraft(emptyDraft());
    setOriginal(null);
    setTab('setup');
  });
  const cancelEdit = () => {
    if (!draft) return;
    if (draft.id == null) { setDraft(null); setOriginal(null); } // auto-open effect reopens the first
    else if (original) setDraft(structuredClone(original));
  };

  // ── save ──────────────────────────────────────────────────────────────────
  const missing = (d: Draft) => {
    const m: string[] = [];
    if (!d.matrixOid) m.push('Matrix');
    if (!d.testName) m.push('Test');
    if (!d.testMethodId) m.push('Method');
    return m;
  };

  /** Save the current draft (no confirm). Returns the id, or null on failure. */
  const saveCurrent = async (): Promise<number | null> => {
    if (!draft) return null;
    setBusy(true);
    try {
      const id = await persist(draft, original);
      resetUnsavedTracking();
      await loadList();
      await loadPattern(id);
      return id;
    } catch (e) {
      notify.error(errMsg(e, 'Save failed.'));
      if (draft.id != null) void loadPattern(draft.id); // show what actually landed
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveFlow = async () => {
    if (!draft || !dirty || busy) return;
    const miss = missing(draft);
    if (miss.length) { notify.warning(`Please fill in: ${miss.join(', ')}.`); return; }
    const ok = await confirm({ title: 'Do you want to Save?', message: 'Do you want to Save?', confirmLabel: 'Yes', cancelLabel: 'No' });
    if (!ok) return;
    if (await saveCurrent() != null) notify.success('Saved successfully.');
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const deleteSel = async () => {
    const ids = checked.size ? [...checked] : curId != null ? [curId] : [];
    if (!ids.length) return;
    const names = ids
      .map((i) => rows.find((r) => r.sequencePatternId === i))
      .filter((r): r is ListRow => !!r)
      .map((r) => `${r.test ?? ''} · ${r.method ?? ''} · ${r.matrix ?? ''}`);
    const ok = await confirm({
      title: 'Delete sequence?',
      message: `Delete ${ids.length} sequence setup${ids.length > 1 ? 's' : ''}?\n${names.join('\n')}`,
      confirmLabel: 'Delete', tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      for (const i of ids) await api.delete(`${BASE}/${i}`);
      notify.success('Deleted.');
      setChecked(new Set());
      if (curId != null && ids.includes(curId)) { setCurId(null); setDraft(null); setOriginal(null); }
      await loadList();
    } catch {
      notify.error('Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const refresh = () => void guard(async () => {
    await loadList();
    if (curId != null) await loadPattern(curId);
    notify.success('Refreshed.');
  });

  // ── Copy From… (load another template's sequence into this draft) ────────
  const copySources = rows.filter((r) => r.sequencePatternId !== draft?.id && configured(r));
  const openCopyFrom = () => {
    if (!draft) return;
    setCopyFromId(copySources[0]?.sequencePatternId ?? null);
    setCopyFromOpen(true);
  };
  const doCopyFrom = async () => {
    if (copyFromId == null || !draft) return;
    if (!draft.testMethodId) { notify.warning('Select a Matrix, Test and Method first.'); return; }
    setCopyFromOpen(false);
    try {
      const s = await fetchPattern(copyFromId);
      // Same rule as the Add picker (and Copy to Selected): only QC types valid for
      // THIS test's method come across; anything else is reported, not dropped silently.
      const valid = new Set(qcTypes.map((t) => t.id));
      const skipped: string[] = [];
      const keep = (phase: Phase, rs: Run[]) => {
        const seen = new Set<string>();
        return asNew(rs.filter((r) => {
          if (!valid.has(r.qcTypeOid)) {
            skipped.push(`${r.qcTypeName || '?'} (${PHASE_TITLE[phase]})`);
            return false;
          }
          // Some older setups list a type twice in a lane; the copy keeps only the first.
          if (seen.has(r.qcTypeOid)) {
            skipped.push(`${r.qcTypeName || '?'} (${PHASE_TITLE[phase]}, listed twice)`);
            return false;
          }
          seen.add(r.qcTypeOid);
          return true;
        }));
      };
      setDraft({
        ...draft,
        freq: s.freq, dupAfter: s.dupAfter, spikeAfter: s.spikeAfter, isClosingQc: s.isClosingQc,
        initial: keep('initial', s.initial), sample: keep('sample', s.sample), closing: keep('closing', s.closing),
      });
      const src = rows.find((r) => r.sequencePatternId === copyFromId);
      notify.info(`Loaded sequence from ${src?.test ?? 'the template'}. Review it, then Save.`);
      if (skipped.length)
        notify.warning(`Not copied (not valid for this test, or listed twice): ${skipped.join(', ')}.`);
    } catch {
      notify.error('Could not load that sequence.');
    }
  };

  // ── Copy Sequence to Selected (bulk) ──────────────────────────────────────
  const copyTargets = [...checked].filter((i) => i !== draft?.id)
    .map((i) => rows.find((r) => r.sequencePatternId === i))
    .filter((r): r is ListRow => !!r);
  const openCopyTo = () => {
    if (!draft || draft.id == null) return;
    if (!copyTargets.length) { notify.warning('Tick other templates on the left first.'); return; }
    setCopyParts({ freq: true, initial: true, sample: true, closing: true });
    setCopyToOpen(true);
  };
  const doCopyTo = async () => {
    const { freq, initial, sample, closing } = copyParts;
    if (!(freq || initial || sample || closing)) { notify.warning('Pick at least one section.'); return; }
    if (!draft) return;
    setCopyToOpen(false);
    // The source's unsaved changes are saved first so every target gets what's on screen
    // (the server copies the SAVED source).
    let srcId = draft.id;
    if (dirty) {
      const miss = missing(draft);
      if (miss.length) { notify.warning(`Please fill in: ${miss.join(', ')} first.`); return; }
      srcId = await saveCurrent();
    }
    if (srcId == null) return;
    // ONE server call, ONE transaction: every target is copied, or none is. Each
    // target only takes QC types valid for its own test; the rest come back as skipped.
    setBusy(true);
    try {
      const res = await api.post(`${BASE}/${srcId}/copy-to`, {
        targetIds: copyTargets.map((t) => t.sequencePatternId),
        frequency: freq, initial, sample, closing,
      });
      const copied = Number(res.data?.data?.copied ?? 0);
      const runsCopied = Number(res.data?.data?.runsCopied ?? 0);
      const skipped = (res.data?.data?.skipped ?? []) as
        { targetId: number; phase: Phase; qcType: string | null; duplicate?: boolean }[];
      const plural = `${copied} template${copied === 1 ? '' : 's'}`;
      // ONE message: plain success only when nothing was skipped; otherwise a single
      // warning that says whether anything was copied at all.
      if (!skipped.length) notify.success(`Copied to ${plural} successfully.`);
      else {
        const byTarget = new Map<number, string[]>();
        for (const s of skipped) {
          const list = byTarget.get(s.targetId) ?? [];
          list.push(`${s.qcType ?? '?'} (${PHASE_TITLE[s.phase] ?? s.phase}${s.duplicate ? ', listed twice' : ''})`);
          byTarget.set(s.targetId, list);
        }
        const lines = [...byTarget].map(([tid, types]) => {
          const r = rows.find((x) => x.sequencePatternId === tid);
          return `${r?.test ?? tid} · ${r?.method ?? ''}: ${types.join(', ')}`;
        });
        const why = `not valid for the target's test, or listed twice in a lane — ${lines.join('; ')}.`;
        const keptNote = ((res.data?.data?.kept ?? []) as unknown[]).length
          ? ' A lane that could take none of them was left unchanged.' : '';
        notify.warning((runsCopied > 0
          ? `Copied to ${plural}, but some QC types were not copied (${why})`
          : `No QC types were copied (${why})`) + keptNote);
      }
      setChecked(new Set());
    } catch (e) {
      notify.error(`${errMsg(e, 'Copy failed.')} Nothing was copied.`);
    } finally {
      setBusy(false);
      await loadList();
      if (curId != null) await loadPattern(curId);
    }
  };

  // ── Sequence Test ─────────────────────────────────────────────────────────
  /** Build the run order from the CURRENT draft (unsaved edits included) on the
   *  server with the real QC-batch builder. `live` = the debounced auto-refresh. */
  const seqReq = useRef(0);
  const runTest = useCallback(async (live: boolean) => {
    if (!draft) return;
    const my = ++seqReq.current;
    const fail = (msg: string) => { setSeqRows([]); setSeqInfo(null); setSeqErr(msg); };
    if (!draft.testMethodId) { fail('Select a Matrix, Test and Method on the Sequence Setup tab first.'); return; }
    if (seqSrc === 'j' && seqJobSel.length === 0) { fail('Pick one or more Job IDs.'); return; }
    setSeqBusy(true);
    try {
      const lane = (rs: Run[], sample: boolean) => rs.map((r) => sample
        ? { qcTypeOid: r.qcTypeOid, frequencyNoSamples: r.freq, insertAfterSampleNo: r.after }
        : { qcTypeOid: r.qcTypeOid });
      const res = await api.post(`${BASE}/sequence-test`, {
        testMethodId: draft.testMethodId,
        isClosingQc: draft.isClosingQc,
        numberOfSamplesBetweenQcTest: draft.freq,
        dupSamplesAfterNoOfSamples: draft.dupAfter,
        spikeSamplesAfterNoOfSamples: draft.spikeAfter,
        templateId: draft.templateId === '' ? null : Number(draft.templateId),
        initial: lane(draft.initial, false),
        sample: lane(draft.sample, true),
        closing: lane(draft.closing, false),
        sampleCount: seqSrc === 'n' ? seqN : null,
        jobOids: seqSrc === 'j' ? seqJobSel : null,
        noRuns: seqRuns,
      }, (live ? { silentLoading: true } : {}) as never);
      if (my !== seqReq.current) return; // a newer run superseded this one
      const info = (res.data?.meta ?? null) as SeqTestInfo | null;
      setSeqRows((res.data?.data ?? []) as SeqTestRow[]);
      setSeqInfo(info);
      setSeqErr('');
      if (!live) notify.info(`Sequence generated — ${info?.total ?? 0} positions.`);
    } catch (e) {
      if (my === seqReq.current) fail(errMsg(e, 'Could not build the sequence.'));
    } finally {
      if (my === seqReq.current) setSeqBusy(false);
    }
  }, [draft, seqSrc, seqN, seqJobSel, seqRuns, notify]);

  // Live: re-run (debounced) whenever the draft or the test inputs change while
  // the tab is open — no need to save and re-test.
  useEffect(() => {
    if (tab !== 'test' || !draft) return;
    const h = setTimeout(() => { void runTest(true); }, 350);
    return () => clearTimeout(h);
  }, [tab, draft, runTest]);

  const resetTest = () => { setSeqSrc('n'); setSeqN(10); setSeqRuns(1); setSeqJobSel([]); };

  useHotkeys({
    combo: 'mod+s', label: 'Save', group: FORM, disabled: !dirty || busy,
    run: () => void saveFlow(),
  });

  // ── render helpers ────────────────────────────────────────────────────────
  const curRow = rows.find((r) => r.sequencePatternId === curId);
  const isNew = draft != null && draft.id == null;
  const title = isNew ? 'New Sequence' : (curRow?.test ?? draft?.testName ?? '—');
  const sub = isNew
    ? 'Pick a matrix, test and method, then build the QC run.'
    : [curRow?.matrix, curRow?.method, draft?.templateId ? `Template ${draft.templateId}` : ''].filter(Boolean).join(' · ');
  const meta = !draft ? '' : isNew ? 'Not saved yet'
    : draft.lastUpdatedBy || draft.lastUpdatedDate
      ? `Last updated by ${draft.lastUpdatedBy || '—'}${draft.lastUpdatedDate ? ` · ${fmtLocalDateTime(draft.lastUpdatedDate)}` : ''}`
      : '';

  const exportData = useMemo(() => ({
    filename: 'SDMS Sequence Setup',
    headers: ['Template ID', 'Test', 'Method', 'Matrix', 'Initial QC', 'Sample QC', 'Closing QC'],
    rows: visible.map((r) => [String(r.templateId ?? ''), r.test ?? '', r.method ?? '', r.matrix ?? '',
      String(r.initialCount), String(r.sampleCount), String(r.closingCount)]),
  }), [visible]);

  return (
    <div className="sqs">
      <div className="sqs-bar">
        <AppToolbar
          left={readOnly ? undefined : <>
            <button type="button" className="ca-btn" disabled={busy || isNew || !draft || checked.size === 0}
              title="Copy this template's QC sequence onto every ticked template" onClick={openCopyTo}>
              <span className="tb-i tb-i-blue">⧉</span> Copy Sequence to Selected{copyTargets.length ? ` (${copyTargets.length})` : ''}
            </button>
            <button type="button" className="ca-btn" disabled={busy || !draft}
              title="Load another template's QC sequence into this one" onClick={openCopyFrom}>
              <span className="tb-i tb-i-blue">⇩</span> Copy From…
            </button>
          </>}
          onNew={readOnly ? undefined : newRecord}
          onDelete={readOnly ? undefined : () => void deleteSel()}
          deleteDisabled={busy || (checked.size === 0 && curId == null)}
          onRefresh={refresh}
          refreshing={listLoading}
          exportData={exportData}
        />
        <FormTitle pageId={pageId} loadMs={loadMs} loading={listLoading}
          auditFormName={FORM} auditSourceId={curId != null ? String(curId) : undefined} />
        {!readOnly && checked.size > 0 && <span className="sqs-selinfo"><b>{checked.size}</b> ticked</span>}
      </div>

      <div className="split">
        {/* ═════════ LEFT: template list ═════════ */}
        <section className="pane lpane" style={{ width: paneW }}>
          <div className="lhead">
            <input className="search" placeholder="🔍  Search test, method, template ID…"
              value={search} onChange={(e) => setSearch(e.target.value)} data-unsaved-ignore />
            <div className="chips">
              {['All', ...matrixChips, 'Not configured'].map((o) => {
                const n = o === 'All' ? rows.length
                  : o === 'Not configured' ? rows.filter((r) => !configured(r)).length
                  : rows.filter((r) => r.matrix === o).length;
                return (
                  <span key={o} className={`chip${filter === o ? ' on' : ''}${o === 'Not configured' ? ' warn' : ''}`}
                    onClick={() => setFilter(o)}>{o}<b>{n}</b></span>
                );
              })}
            </div>
          </div>
          <div className="lscroll" tabIndex={0} onKeyDown={onListKey}>
            <table id="sqsList">
              <colgroup>
                {!readOnly && <col style={{ width: 30 }} />}<col style={{ width: 90 }} /><col />
                <col style={{ width: 104 }} /><col style={{ width: 66 }} /><col style={{ width: 84 }} />
              </colgroup>
              <thead>
                <tr>
                  {!readOnly && (
                    <th className="cbcol">
                      <input type="checkbox" checked={allVisibleChecked} onChange={toggleAllVisible}
                        aria-label="Select all visible" data-unsaved-ignore />
                    </th>
                  )}
                  {([['templateId', 'Template ID'], ['test', 'Test'], ['method', 'Method'], ['matrix', 'Matrix']] as const)
                    .map(([k, l]) => (
                      <th key={k} className="srt" onClick={() => onSort(k)} title={l}>
                        <div className="thc"><span>{l}</span>
                          <span className="caret">{sortK === k ? (sortDir > 0 ? '↑' : '↓') : '↕'}</span></div>
                      </th>
                    ))}
                  <th title="Initial · Sample · Closing QC count">QC I · S · C</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.sequencePatternId}
                    className={`${r.sequencePatternId === curId && !isNew ? 'cur' : ''} ${checked.has(r.sequencePatternId) ? 'chk' : ''}`}
                    onClick={(e) => (!readOnly && (e.ctrlKey || e.metaKey || e.shiftKey))
                      ? toggleCheck(r.sequencePatternId) : openRow(r.sequencePatternId)}>
                    {!readOnly && (
                      <td className="cbcol" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={checked.has(r.sequencePatternId)}
                          onChange={() => toggleCheck(r.sequencePatternId)} aria-label="Select row" data-unsaved-ignore />
                      </td>
                    )}
                    <td>{r.templateId ?? <span style={{ color: '#c3c9d2' }}>—</span>}</td>
                    <td title={r.test ?? ''}>{r.test}</td>
                    <td title={r.method ?? ''}>{r.method}</td>
                    <td title={r.matrix ?? ''}>{r.matrix}</td>
                    <td>
                      {configured(r) ? (
                        <span className="qcsum">
                          <i className={r.initialCount ? '' : 'z'}>{r.initialCount}</i>
                          <i className={r.sampleCount ? '' : 'z'}>{r.sampleCount}</i>
                          <i className={r.closingCount ? '' : 'z'}>{r.closingCount}</i>
                        </span>
                      ) : <span className="pill">Not set</span>}
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={readOnly ? 5 : 6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>
                    {listLoading ? 'Loading…' : 'No templates match.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="lfoot">
            <span>{visible.length} of {rows.length} templates</span>
            {readOnly
              ? <span><span className="kbd">↑</span> <span className="kbd">↓</span> move · view only</span>
              : <span><span className="kbd">↑</span> <span className="kbd">↓</span> move · <span className="kbd">Space</span> tick ·{' '}
                <span className="kbd">Ctrl</span>+<span className="kbd">S</span> save</span>}
          </div>
        </section>

        <div className="gutter" title="Drag to resize" onMouseDown={onGutterDown} />

        {/* ═════════ RIGHT: live editor ═════════ */}
        <section className="pane rpane">
          <div className="dhead">
            <div><h2>{title}</h2><div className="sub">{sub}</div></div>
            {dirty && <span className="dirty">● Unsaved changes</span>}
            {!readOnly && (
              <div className="dacts">
                <button type="button" className="ca-btn" disabled={!dirty || busy} onClick={cancelEdit}>Cancel</button>
                <button type="button" className="ca-btn ca-btn-solid" disabled={!dirty || busy} onClick={() => void saveFlow()}>💾 Save</button>
              </div>
            )}
          </div>
          <div className="tabs" role="tablist">
            <button type="button" role="tab" className={`tab${tab === 'setup' ? ' on' : ''}`}
              aria-selected={tab === 'setup'} onClick={() => setTab('setup')}>Sequence Setup</button>
            {!readOnly && (
              <button type="button" role="tab" className={`tab${tab === 'test' ? ' on' : ''}`}
                aria-selected={tab === 'test'} onClick={() => setTab('test')}>Sequence Test</button>
            )}
          </div>

          <div className="dbody">
            {!draft ? (
              <div className="placeholder">{listLoading || busy ? 'Loading…' : 'Select a sequence setup, or press New.'}</div>
            ) : tab === 'test' && !readOnly ? (
              // Test inputs are not record edits — keep them out of the unsaved-changes tracker.
              <div data-unsaved-ignore style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div className="card">
                  <div className="sec-bar">Test</div>
                  <div className="stest">
                    <div className="srcs">
                      <label className="rad">
                        <input type="radio" name="sqsSrc" checked={seqSrc === 'n'} onChange={() => setSeqSrc('n')} />
                        Insert Number of samples
                      </label>
                      <NumBox value={seqN} max={500} disabled={seqSrc !== 'n'} onChange={setSeqN} />
                      <label className="rad">
                        <input type="radio" name="sqsSrc" checked={seqSrc === 'j'} onChange={() => setSeqSrc('j')} />
                        Select Job IDs
                      </label>
                      <select className="fin" multiple size={3} disabled={seqSrc !== 'j'} value={seqJobSel}
                        title={jobs.length ? undefined : 'No jobs have samples pending for this test'}
                        onChange={(e) => setSeqJobSel([...e.target.selectedOptions].map((o) => o.value))}>
                        {jobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.name} — {j.sampleCount} sample{j.sampleCount === 1 ? '' : 's'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="runno"><span>No Of Run</span><NumBox value={seqRuns} max={20} onChange={setSeqRuns} /></div>
                    <div className="stbtns">
                      <button type="button" className="ca-btn" onClick={resetTest}>⟳ Reset</button>
                      <button type="button" className="ca-btn ca-btn-solid" disabled={seqBusy} onClick={() => void runTest(false)}>▶ Test</button>
                    </div>
                  </div>
                  <div className={`stnote${seqErr ? ' err' : ''}`}>
                    {seqErr || 'Uses the setup on the Sequence Setup tab as it is now, including changes you haven\'t saved. '
                      + 'Built by the same sequence builder a QC batch Sort uses: initial QC once, each sample (plus its '
                      + 'No Of Run replicate runs), Sample QC after sample n when n ≥ InsertAfter and (n − InsertAfter) is a '
                      + 'multiple of RunEvery, closing QC once at the end when Is Closing QC is ticked. A Prep Batch Sort places Sample QC '
                      + 'by the same RunEvery / InsertAfter rule.'}
                  </div>
                </div>

                <div className="card seqdisp">
                  <div className="sec-bar">Sequence Display
                    <span className="hint">
                      {seqInfo ? `— ${seqInfo.samples} sample${seqInfo.samples === 1 ? '' : 's'} + ${seqInfo.qc} QC = ${seqInfo.total} positions`
                        + (seqInfo.noRuns > 1 ? ` · ${seqInfo.noRuns} runs per sample` : '') : ''}
                    </span>
                    <span className="legend">
                      <span><i style={{ background: 'var(--r-std)' }} />Standard</span>
                      <span><i style={{ background: 'var(--r-blk)' }} />Blank</span>
                      <span><i style={{ background: 'var(--r-spk)' }} />Spike</span>
                      <span><i style={{ background: 'var(--r-dup)' }} />Duplicate</span>
                      <span><i style={{ background: '#fff', border: '1px solid #c9ced6' }} />Sample</span>
                    </span>
                  </div>
                  <div className="lscroll">
                    <table id="seqT">
                      <thead>
                        <tr><th style={{ width: 44 }}>#</th><th>QC Type</th><th>Sample ID</th><th style={{ width: 120 }}>Run Number</th></tr>
                      </thead>
                      <tbody>
                        {seqRows.map((r, i) => {
                          const isQc = r.kind === 'qc';
                          const tok = !isQc ? 'Sample' : r.qcRole && ROLE_TOKENS.has(r.qcRole) ? r.qcRole : 'Other';
                          return (
                            <tr key={`${r.sort}-${i}`} className={isQc ? 'qcrow' : 'sm'}>
                              <td style={{ color: 'var(--muted)' }}>{i + 1}</td>
                              <td><span className={`tok ${tok}`}>{r.qcType}</span></td>
                              <td>{r.sampleId}</td>
                              <td><span className={`rn${r.runno > 1 ? ' hi' : ''}`}>{r.runno}</span></td>
                            </tr>
                          );
                        })}
                        {seqRows.length === 0 && (
                          <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>
                            {seqBusy ? 'Building…' : 'Nothing to show.'}
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="card">
                  <div className="sec-bar">Test</div>
                  <div className="testgrid">
                    <div className="fcol">
                      <label className="req">Matrix</label>
                      <select className={`fin${draft.matrixOid ? '' : ' reqempty'}`} value={draft.matrixOid}
                        disabled={readOnly} onChange={(e) => setMatrix(e.target.value)}>
                        <option value="" />
                        {matrices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                      <label className="req">Test</label>
                      <select className={`fin${draft.testName ? '' : ' reqempty'}`} value={draft.testName}
                        disabled={readOnly || !draft.matrixOid} onChange={(e) => setTest(e.target.value)}>
                        <option value="" />
                        {tests.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        {draft.testName && !tests.some((o) => o.id === draft.testName)
                          && <option value={draft.testName}>{draft.testName}</option>}
                      </select>
                      <label className="req">Method</label>
                      <select className={`fin${draft.testMethodId ? '' : ' reqempty'}`} value={draft.testMethodId}
                        disabled={readOnly || !draft.testName} onChange={(e) => patch({ testMethodId: e.target.value, templateId: '' })}>
                        <option value="" />
                        {methods.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        {draft.testMethodId && !methods.some((o) => o.id === draft.testMethodId)
                          && <option value={draft.testMethodId}>{draft.methodName || '…'}</option>}
                      </select>
                      <label>Template ID</label>
                      {/* OG NPTemplateID: a lookup of this test's templates that still allows typing an ID. */}
                      <input className="fin" list="sqsTplList" inputMode="numeric" value={draft.templateId}
                        readOnly={readOnly} disabled={readOnly} onChange={(e) => patch({ templateId: e.target.value.replace(/[^\d]/g, '') })} />
                      <datalist id="sqsTplList">
                        {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name ?? ''}</option>)}
                      </datalist>
                    </div>
                    <div className="rcol">
                      <span>Frequency (F) of QC analyses, once per every</span>
                      <NumBox value={draft.freq} disabled={readOnly} onChange={(v) => patch({ freq: v })} /><span>Samples</span>
                      <span>Insert a duplicate sample in a batch after the No.</span>
                      <NumBox value={draft.dupAfter} disabled={readOnly} onChange={(v) => patch({ dupAfter: v })} /><span>Sample</span>
                      <span>Insert a spike sample in a batch after the No.</span>
                      <NumBox value={draft.spikeAfter} disabled={readOnly} onChange={(v) => patch({ spikeAfter: v })} /><span>Sample</span>
                    </div>
                  </div>
                </div>

                <div className="card qcpref">
                  <div className="sec-bar">Rate QC Preference
                    {!readOnly && (
                      <span className="hint">drag ⠿ to reorder · pick a QC type to add · QCRole / QCRootRole fill in from the QC Type setup</span>
                    )}
                  </div>
                  <div className="lanes">
                    {PHASES.map((p) => (
                      <Lane key={p} phase={p}
                        title={p === 'initial' ? 'Initial QC test run' : p === 'sample' ? 'Sample QC test run' : 'Closing QC test run'}
                        when={p === 'initial' ? 'once, before the first sample'
                          : p === 'sample' ? 'after sample Insert After, then every Run Every samples'
                          : draft.isClosingQc ? 'at the end of the run' : readOnly ? 'off' : 'off — tick to run'}
                        rows={draft[p]} qcTypes={qcTypes} canAdd={!!draft.testMethodId} readOnly={readOnly}
                        closingOn={draft.isClosingQc} onClosing={(v) => patch({ isClosingQc: v })}
                        onAdd={addQc} onChangeType={changeType} onRemove={removeQc} onMove={moveQc}
                        onNum={setRunNum} onPreset={applyPreset} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="dfoot">
            <span className="meta">{meta}</span>
            {!readOnly && (
              <>
                <button type="button" className="ca-btn" disabled={!dirty || busy} onClick={cancelEdit}>Cancel</button>
                <button type="button" className="ca-btn ca-btn-solid" disabled={!dirty || busy} onClick={() => void saveFlow()}>💾 Save</button>
              </>
            )}
          </div>
        </section>
      </div>

      {/* ── Copy From… ── */}
      <AppDialog open={copyFromOpen} title="Copy From…" onClose={() => setCopyFromOpen(false)} width={520}
        className="sqs-dlg"
        footer={<>
          <button type="button" className="ca-btn" onClick={() => setCopyFromOpen(false)}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" disabled={copyFromId == null || !draft?.testMethodId}
            onClick={() => void doCopyFrom()}>Load</button>
        </>}>
        <p style={{ margin: '0 0 10px' }}>
          Load another template's QC sequence into <b>{draft?.testName || 'this sequence'}</b>. Nothing is saved until you press Save.
          QC types that are not valid for this test are left out.
        </p>
        {!draft?.testMethodId && <p className="warn">Select a Matrix, Test and Method first.</p>}
        {copySources.length ? (
          <select className="fin" value={copyFromId ?? ''} onChange={(e) => setCopyFromId(Number(e.target.value))}>
            {copySources.map((r) => (
              <option key={r.sequencePatternId} value={r.sequencePatternId}>
                {r.test} · {r.method} · {r.matrix}{r.templateId != null ? ` · ${r.templateId}` : ''} (I{r.initialCount} S{r.sampleCount} C{r.closingCount})
              </option>
            ))}
          </select>
        ) : <p className="warn">No other template has a QC sequence yet.</p>}
      </AppDialog>

      {/* ── Copy Sequence to Selected ── */}
      <AppDialog open={copyToOpen} onClose={() => setCopyToOpen(false)} width={620} className="sqs-dlg"
        title={`Copy QC sequence to ${copyTargets.length} template${copyTargets.length > 1 ? 's' : ''}`}
        footer={<>
          <button type="button" className="ca-btn" onClick={() => setCopyToOpen(false)}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => void doCopyTo()}>Copy to {copyTargets.length}</button>
        </>}>
        {draft && (
          <>
            <p style={{ margin: '0 0 10px' }}>
              From <b>{draft.testName}</b> ({curRow?.method}, {curRow?.matrix}). Choose what to copy:
            </p>
            {([
              ['freq', 'Frequency', `Once every ${draft.freq} samples · duplicate after ${draft.dupAfter} · spike after ${draft.spikeAfter}`],
              ['initial', 'Initial QC', draft.initial.map((r) => r.qcTypeName).join(' · ') || 'none'],
              ['sample', 'Sample QC', draft.sample.map((r) => r.qcTypeName).join(' · ') || 'none'],
              ['closing', 'Closing QC', `${draft.isClosingQc ? 'On' : 'Off'} — ${draft.closing.map((r) => r.qcTypeName).join(' · ') || 'none'}`],
            ] as const).map(([k, l, s]) => (
              <label key={k} className="opt">
                <input type="checkbox" checked={copyParts[k]}
                  onChange={(e) => setCopyParts((p) => ({ ...p, [k]: e.target.checked }))} />
                <span>{l}<small>{s}</small></span>
              </label>
            ))}
            <div style={{ fontSize: 12, margin: '8px 0 4px' }}>Targets</div>
            <div className="tgtlist">
              {copyTargets.map((r) => (
                <div key={r.sequencePatternId}>
                  <span>{r.test} · {r.method} · {r.matrix}</span>
                  {configured(r) && <span className="pill">will overwrite</span>}
                </div>
              ))}
            </div>
            {copyTargets.some(configured) && (
              <p className="warn">⚠ {copyTargets.filter(configured).length} target(s) already have a sequence. The ticked sections will be <b>replaced</b>.</p>
            )}
            {dirty && <p className="warn">The source has unsaved changes. They will be saved first.</p>}
          </>
        )}
      </AppDialog>

      {/* ── Quick preset: replace or append ── */}
      <AppDialog open={presetAsk != null} title="Replace or append?" onClose={() => setPresetAsk(null)} width={420}
        className="sqs-dlg"
        footer={<>
          <button type="button" className="ca-btn" onClick={() => setPresetAsk(null)}>Cancel</button>
          <button type="button" className="ca-btn" onClick={() => resolvePreset('append')}>Append</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => resolvePreset('replace')}>Replace</button>
        </>}>
        <p style={{ margin: 0 }}>
          This grid already has {presetAsk ? draft?.[presetAsk.phase].length ?? 0 : 0} row(s)
          {presetAsk ? ` in ${PHASE_TITLE[presetAsk.phase]}` : ''}.
        </p>
      </AppDialog>
    </div>
  );
}

// ── small parts ───────────────────────────────────────────────────────────────

function NumBox({ value, onChange, max, disabled }: {
  value: number; onChange: (v: number) => void; max?: number; disabled?: boolean;
}) {
  const set = (v: number) => onChange(max != null ? Math.min(max, v) : v);
  return (
    <span className="num" style={disabled ? { opacity: 0.5 } : undefined}>
      <button type="button" disabled={disabled} onClick={() => set(Math.max(1, value - 1))} aria-label="Decrease">−</button>
      <input inputMode="numeric" value={value} disabled={disabled} onChange={(e) => set(clampInt(e.target.value))} />
      <button type="button" disabled={disabled} onClick={() => set(value + 1)} aria-label="Increase">+</button>
    </span>
  );
}

function Lane({
  phase, title, when, rows, qcTypes, canAdd, readOnly = false, closingOn, onClosing,
  onAdd, onChangeType, onRemove, onMove, onNum, onPreset,
}: {
  phase: Phase;
  title: string;
  when: string;
  rows: Run[];
  qcTypes: QcTypeOpt[];
  canAdd: boolean;
  /** View only (Data Center): no drag, no type / number editing, no remove, no add bar. */
  readOnly?: boolean;
  closingOn: boolean;
  onClosing: (v: boolean) => void;
  onAdd: (p: Phase, qcTypeOid: string) => void;
  onChangeType: (p: Phase, idx: number, qcTypeOid: string) => void;
  onRemove: (p: Phase, idx: number) => void;
  onMove: (p: Phase, from: number, to: number) => void;
  onNum: (p: Phase, idx: number, k: 'freq' | 'after', v: unknown) => void;
  onPreset: (p: Phase, label: string) => void;
}) {
  const isSample = phase === 'sample';
  const off = phase === 'closing' && !closingOn;
  const dragFrom = useRef<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [focus, setFocus] = useState(0);
  const cls = phase === 'initial' ? 'init' : phase === 'sample' ? 'sample' : 'close';
  const focusIdx = rows.length ? Math.min(focus, rows.length - 1) : -1;

  // Rows flagged: a type listed twice in this lane (only legacy setups can still
  // hold one). The prototype's "InsertAfter > F" flag is NOT shown — F is the Prep
  // Batch block size and the QC Batch rule never reads it (owner decision 2026-09-25).
  const seen = new Set<string>();
  const warns = rows.map((r) => {
    const dup = seen.has(r.qcTypeOid); seen.add(r.qcTypeOid);
    return { dup };
  });

  return (
    <div className={`lane ${cls}${off ? ' off' : ''}`}>
      <div className="lh">
        <b>{title}</b>
        {phase === 'closing' && (
          <label className="cbx" title="Run Closing QC at the end of the run">
            <input type="checkbox" checked={closingOn} disabled={readOnly} onChange={(e) => onClosing(e.target.checked)} />
          </label>
        )}
        <span className="cnt">{rows.length}</span>
        <span className="when">{when}</span>
      </div>
      <div className="lt">
        {rows.length ? (
          <table>
            <thead>
              <tr>
                {!readOnly && <th />}
                <th>QCType</th><th>QCRole</th><th>QCRoot Role</th><th>Order</th>
                {isSample && <>
                  <th title="After the first one, repeat this QC every N samples">RunEvery (#)Samples</th>
                  <th title="Place this QC first after sample number N">InsertAfter Sample#</th>
                </>}
                {!readOnly && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const w = warns[i]!;
                // A row can switch only to a type NOT already in this lane (OG: each QC
                // type once per lane) — its own type always stays in the list.
                const others = new Set(rows.filter((_, j) => j !== i).map((x) => x.qcTypeOid));
                const free = qcTypes.filter((t) => t.id === r.qcTypeOid || !others.has(t.id));
                const opts = free.some((t) => t.id === r.qcTypeOid)
                  ? free : [{ id: r.qcTypeOid, name: r.qcTypeName }, ...free];
                return (
                  <tr key={r.key} draggable={!readOnly}
                    className={`${over === i ? 'over' : ''} ${w.dup ? 'warnrow' : ''} ${focusIdx === i ? 'cur' : ''}`}
                    onClick={() => setFocus(i)}
                    onDragStart={readOnly ? undefined : (e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; }}
                    onDragEnd={readOnly ? undefined : () => { dragFrom.current = null; setOver(null); }}
                    onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); setOver(i); }}
                    onDragLeave={readOnly ? undefined : () => setOver((o) => (o === i ? null : o))}
                    onDrop={readOnly ? undefined : (e) => {
                      e.preventDefault();
                      if (dragFrom.current != null) onMove(phase, dragFrom.current, i);
                      dragFrom.current = null; setOver(null); setFocus(i);
                    }}>
                    {!readOnly && <td className="hnd" title="Drag to reorder">⠿</td>}
                    <td>
                      {readOnly ? <span className="rtx">{r.qcTypeName}</span> : (
                        <select className="qsel" value={r.qcTypeOid} onChange={(e) => onChangeType(phase, i, e.target.value)}>
                          {opts.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      )}
                      {w.dup && <div className="wtxt">Listed twice</div>}
                    </td>
                    <td className="rtx"><span className={`rdot ${r.qcRole}`} />{r.qcRole}</td>
                    <td className="rtx">{r.qcRootRole}</td>
                    <td className="ord">{i + 1}</td>
                    {isSample && (readOnly ? (
                      <>
                        <td className="nc">{r.freq}</td>
                        <td className="nc">{r.after}</td>
                      </>
                    ) : (
                      <>
                        <td className="nc"><input type="number" min={1} className="cellnum" value={r.freq}
                          onChange={(e) => onNum(phase, i, 'freq', e.target.value)} aria-label="RunEvery(#)Samples" /></td>
                        <td className="nc"><input type="number" min={1} className="cellnum" value={r.after}
                          onChange={(e) => onNum(phase, i, 'after', e.target.value)} aria-label="InsertAfterSample#" /></td>
                      </>
                    ))}
                    {!readOnly && (
                      <td className="ctl"><button type="button" className="rm" title="Remove"
                        onClick={(e) => { e.stopPropagation(); onRemove(phase, i); }}>×</button></td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="lempty">{readOnly ? 'No QC.' : <>No QC yet.<br />Pick a QC type below, or use a Quick preset.</>}</div>
        )}
      </div>
      {!readOnly && <div className="ladd">
        <select value="" disabled={!canAdd} title={canAdd ? undefined : 'Select a Matrix, Test and Method first'}
          onChange={(e) => { onAdd(phase, e.target.value); e.target.value = ''; }}>
          <option value="">＋ Add QC type…</option>
          {/* OG ADD picker hides the types already in this lane (Sequencesetupcontroller :165-190). */}
          {qcTypes.filter((t) => !rows.some((r) => r.qcTypeOid === t.id))
            .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="quick">Quick:
          {PRESETS[phase].map((p) => (
            <a key={p.label} onClick={() => canAdd && onPreset(phase, p.label)}>{p.label}</a>
          ))}
        </span>
      </div>}
      <div className="lrec">Record {focusIdx + 1} of {rows.length}</div>
    </div>
  );
}
